// Lista de precos de uma tabela, calculados pela regra de src/preco-tabela.js.
//
// POR QUE EXISTE: toda tabela do banco chega a tela com `itens: []`
// (mapearTabelaPreco) -- a lista e resto do prototipo. A aba de tabela do
// negocio passou a calcular o preco em 16/09; a aba e o cartao do cliente
// continuaram lendo a lista vazia e mostravam uma tabela sem linhas e "0 itens"
// (Kaio, 29/09: "pode seguir com as 3"). Um lugar so, para as telas nao
// voltarem a divergir.
import React from 'react';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { brlExato } from './data.jsx';
import { Input } from './modals.jsx';
import { precoNaTabela, precoBase } from './preco-tabela.js';

const { useState, useEffect } = React;

// Manual tem preco proprio por produto e precisa dos itens; fator nao tem itens
// -- o preco dele e conta sobre a base. Produto sem preco fica de fora: entrou
// sem preco de proposito (48,3% do catalogo so tem preco numa tabela inativa do
// ERP) e mostra-lo com "—" encheria a lista de linhas vazias.
export function useLinhasDaTabela(tabela) {
  const store = useStore();
  const [itensManuais, setItensManuais] = useState([]);

  useEffect(() => {
    let vivo = true;
    if (tabela?.tipo !== 'manual') { setItensManuais([]); return; }
    store.carregarItensTabela(tabela.id).then((linhas) => { if (vivo) setItensManuais(linhas); });
    return () => { vivo = false; };
  }, [store, tabela?.id, tabela?.tipo]);

  if (!tabela) return [];
  if (tabela.tipo === 'manual') {
    return itensManuais.map((it) => ({
      chave: it.id,
      nome: it.produto_nome,
      unidade: it.unidade || 'un',
      base: it.preco_lista == null ? null : Number(it.preco_lista),
      preco: Number(it.preco_tabela),
    }));
  }
  return (store.produtos || [])
    .map((p) => ({ chave: p.id, nome: p.nome, unidade: p.unidade || 'un', base: precoBase(p), preco: precoNaTabela(p, tabela) }))
    .filter((l) => l.preco != null);
}

// Quantos produtos tem preco em cada tabela -- a coluna "Itens" de Ajustes.
// Conta pela mesma regra da lista (precoNaTabela), e nao por uma copia dela: a
// contagem e a lista nao podem discordar. Manual conta os itens carregados.
export function useContagemDePrecos(tabelas) {
  const store = useStore();
  const produtos = store.produtos;
  const [manuais, setManuais] = useState({});
  const idsManuais = (tabelas || []).filter((t) => t.tipo === 'manual').map((t) => t.id).join(',');

  useEffect(() => {
    let vivo = true;
    for (const id of idsManuais ? idsManuais.split(',') : []) {
      store.carregarItensTabela(id).then((linhas) => { if (vivo) setManuais((m) => ({ ...m, [id]: linhas.length })); });
    }
    return () => { vivo = false; };
  }, [store, idsManuais]);

  const calculadas = React.useMemo(() => {
    const porTabela = {};
    for (const t of tabelas || []) {
      if (t.tipo === 'manual') continue;
      porTabela[t.id] = (produtos || []).reduce((n, p) => (precoNaTabela(p, t) == null ? n : n + 1), 0);
    }
    return porTabela;
  }, [tabelas, produtos]);

  return { ...calculadas, ...manuais };
}

export function TabelaDeLinhas({ linhas, limite, dica = 'Use a busca para encontrar um específico.' }) {
  return (
    <>
      <table className="tabela-precos">
        <thead><tr><th>Produto</th><th>Un.</th><th style={{ textAlign: 'right' }}>Base</th><th style={{ textAlign: 'right' }}>Preço da tabela</th></tr></thead>
        <tbody>
          {linhas.slice(0, limite).map((l) => (
            <tr key={l.chave}>
              <td><div style={{ fontWeight: 600, fontSize: 13 }}>{l.nome}</div></td>
              <td><span className="mono" style={{ fontSize: 11.5, color: 'var(--color-ink-soft)' }}>{l.unidade}</span></td>
              <td style={{ textAlign: 'right' }}>
                <span className="mono" style={{ color: 'var(--color-ink-faint)', whiteSpace: 'nowrap' }}>{l.base == null ? '—' : brlExato(l.base)}</span>
              </td>
              <td style={{ textAlign: 'right' }}><span className="mono" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{brlExato(l.preco)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {linhas.length > limite && (
        <div className="tabela-foot-note">
          <Icon name="info" size={11} />
          Mostrando {limite} de {linhas.length.toLocaleString('pt-BR')} produtos.{dica ? ` ${dica}` : ''}
        </div>
      )}
    </>
  );
}

// Lista com busca, como a aba do negocio sempre mostrou.
export function ListaDePrecos({ tabela, limite = 60 }) {
  const linhas = useLinhasDaTabela(tabela);
  const [busca, setBusca] = useState('');
  const termo = busca.trim().toLowerCase();
  const filtradas = linhas.filter((l) => !termo || String(l.nome).toLowerCase().includes(termo));
  return (
    <>
      <div style={{ padding: '0 14px 10px' }}>
        <Input icon="search" placeholder="Buscar produto…" value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>
      {filtradas.length === 0 ? (
        <div className="empty" style={{ padding: 24 }}>
          {termo ? 'Nenhum produto com esse nome nesta tabela.' : 'Esta tabela ainda não tem produto com preço.'}
        </div>
      ) : <TabelaDeLinhas linhas={filtradas} limite={limite} />}
    </>
  );
}
